// Eskom PARCC — diff two CES evaluation-sheet snapshots (PURE).
//
// Each snapshot's `fullEvaluation` blob is `{ cesRows, findings }` (useCaptureCes).
// This compares the captured rows of an EARLIER snapshot against a LATER one, keyed
// on the CES row's `seq` (falling back to its criterion text), and reports which
// criteria were added, removed, or had their status moved, plus the change in the
// findings count. Tolerant by construction: a null or malformed blob yields an
// empty diff, never a throw — legacy snapshots must not crash the flyout.

/** The changed-status shape rendered in the flyout's Changes tab. */
export interface CesSnapshotDiff {
  /** Criteria present in the later snapshot but not the earlier one. */
  added: string[];
  /** Criteria present in the earlier snapshot but gone from the later one. */
  removed: string[];
  /** Criteria whose status moved between the two snapshots. */
  changed: { criterion: string; from: string; to: string }[];
  /** later.findings.length − earlier.findings.length. */
  findingsDelta: number;
}

interface CesRowLite {
  seq?: number;
  criterion?: string;
  status?: string;
}

function rowsOf(full: unknown): CesRowLite[] {
  if (full && typeof full === 'object' && Array.isArray((full as { cesRows?: unknown }).cesRows)) {
    return (full as { cesRows: CesRowLite[] }).cesRows;
  }
  return [];
}

function findingsCount(full: unknown): number {
  if (full && typeof full === 'object' && Array.isArray((full as { findings?: unknown }).findings)) {
    return (full as { findings: unknown[] }).findings.length;
  }
  return 0;
}

/** Match rows across snapshots by seq first (stable), else by folded criterion text. */
function keyOf(r: CesRowLite): string {
  return r.seq != null ? `seq:${r.seq}` : `crit:${(r.criterion ?? '').trim().toLowerCase()}`;
}

function labelOf(r: CesRowLite): string {
  return (r.criterion ?? '').trim() || `Criterion #${r.seq ?? '?'}`;
}

function statusOf(r: CesRowLite): string {
  const s = (r.status ?? '').trim();
  return s || '—';
}

export function diffCesSnapshot(earlier: unknown, later: unknown): CesSnapshotDiff {
  const prev = new Map(rowsOf(earlier).map((r) => [keyOf(r), r]));
  const next = new Map(rowsOf(later).map((r) => [keyOf(r), r]));

  const added: string[] = [];
  const changed: { criterion: string; from: string; to: string }[] = [];
  for (const [k, r] of next) {
    const p = prev.get(k);
    if (!p) added.push(labelOf(r));
    else if (statusOf(p) !== statusOf(r)) {
      changed.push({ criterion: labelOf(r), from: statusOf(p), to: statusOf(r) });
    }
  }

  const removed: string[] = [];
  for (const [k, r] of prev) {
    if (!next.has(k)) removed.push(labelOf(r));
  }

  return { added, removed, changed, findingsDelta: findingsCount(later) - findingsCount(earlier) };
}
